/**
 * The Memory control's picture engine (D340): where every node is on the screen for the globe, the map and the Timeline,
 * how the places blend from one picture to the next, and how the camera moves. No React; the canvas component drives it
 * and `memoryBrainPaint.ts` draws what it holds.
 *
 * From RUBRIC Agentic OS v2 (Jay E, RoboNuggets, MIT; see THIRD-PARTY-NOTICES.md), brain/port.js: one canvas draws every
 * picture; each frame a node's place is asked of the picture (`orbPoint`, `mapPoint`, `timePoint`), the places seen
 * on screen are kept, and switching picture (`setView`, `setLayout`) blends the kept places into the new ones over a
 * second. `rotate` / `project` are the globe's camera; the perspective is `1 / (1 - z / max(900, r * 4.2))`.
 */
import {
  CAMERA_HOME,
  clampCamera,
  type Globe,
  type GlobeCamera,
  type GlobeLayer,
  type GlobeNode,
  LAYER_RADIUS,
  positionOf,
  RINGS_AT_REST,
  type RingState,
  rotatePoint,
} from './memoryGlobeModel.js';
import { bubbleRadius, fitMap, type MapLayout, mapRadiusOf, ringsPlace } from './memoryMapLayouts.js';
import { type GlobeRim, globeRim, type OpenFocus } from './memoryRimModel.js';
import type { TimelineLayout } from './memoryTimelineModel.js';
import { blendPlaces, easeInOutCubic, easeOutCubic, tweenProgress } from './memoryTween.js';

export type Picture = 'globe' | 'map' | 'timeline';

export interface FlatCamera {
  k: number;
  x: number;
  y: number;
}

/** The stage: its size and what the docked panels take at either edge. */
export interface Stage {
  W: number;
  H: number;
  insetLeft: number;
  insetRight: number;
  /** The header's height: the picture centres below it. */
  top: number;
  /** The legend's height at the foot. */
  bottom: number;
}

export const roomOf = (s: Stage) => ({ x: s.insetLeft, w: Math.max(200, s.W - s.insetLeft - s.insetRight), y: s.top, h: Math.max(160, s.H - s.top - s.bottom) });

export interface Engine {
  n: number;
  /** Where each node is drawn this frame, its size, depth (globe: towards the viewer, px) and how visible it is. */
  X: Float32Array;
  Y: Float32Array;
  R: Float32Array;
  Z: Float32Array;
  A: Float32Array;
  /** Where each wants to be. */
  TX: Float32Array;
  TY: Float32Array;
  TR: Float32Array;
  TZ: Float32Array;
  TA: Float32Array;
  /** Where it was when the picture changed. */
  fx: Float32Array;
  fy: Float32Array;
  fr: Float32Array;
  /** A fraction per node, so a layout change ripples instead of moving as a block. */
  delay: Float32Array;
  /** A stable fraction of each node's name. */
  seed: Float32Array;
  fresh: Uint8Array;
  trans: { t0: number; dur: number } | null;
  /** Globe camera and its tween (`follow`: the node the camera is chasing, since it moves). */
  cam: GlobeCamera;
  camTween: { t0: number; dur: number; from: GlobeCamera; to: GlobeCamera; follow?: number } | null;
  /** Map and Timeline camera and its tween. */
  flat: FlatCamera;
  flatTween: { t0: number; dur: number; from: FlatCamera; to: FlatCamera } | null;
  rings: RingState;
  /** How far the Rings layout has turned, radians. */
  ringRot: number;
  clock: number;
  picture: Picture;
  /** The centre: where the logo sits this frame. */
  cx: number;
  cy: number;
  /** The globe's radius in px at zoom 1, and its centre. */
  gR: number;
  hover: number;
  /** The Universe in the rim state this frame, when a level is open. */
  rim: GlobeRim | null;
  /** The selected node and what it draws: set by the painter's owner. */
  drag: { x: number; y: number; moved: boolean; button: number } | null;
  /** Dirty: something changed that needs a draw. */
  dirty: boolean;
  /** The picture last asked for, to know when it changed. */
  key: string;
  /** The flat camera's zoom that fits the whole picture: names of things wait until the zoom is well past it. */
  fitK: number;
}

function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

export function createEngine(globe: Globe): Engine {
  const n = globe.nodes.length;
  const f = () => new Float32Array(n);
  const e: Engine = {
    n,
    X: f(),
    Y: f(),
    R: f(),
    Z: f(),
    A: f(),
    TX: f(),
    TY: f(),
    TR: f(),
    TZ: f(),
    TA: f(),
    fx: f(),
    fy: f(),
    fr: f(),
    delay: f(),
    seed: f(),
    fresh: new Uint8Array(n),
    trans: null,
    cam: { ...CAMERA_HOME },
    camTween: null,
    flat: { k: 1, x: 0, y: 0 },
    flatTween: null,
    rings: RINGS_AT_REST,
    ringRot: 0,
    clock: 0,
    picture: 'globe',
    cx: 0,
    cy: 0,
    gR: 300,
    hover: -1,
    rim: null,
    drag: null,
    dirty: true,
    key: '',
    fitK: 1,
  };
  globe.nodes.forEach((node, i) => {
    e.seed[i] = hash01(node.id);
    e.delay[i] = hash01(`${node.id}#`);
    e.A[i] = 1;
  });
  return e;
}

/**
 * A graph that changed under a drawn picture (round 4): nodes whose key exists in both keep where they are drawn and
 * glide to their new place, new ones enter in place and fade in, gone ones leave. The camera and the picture are kept.
 */
export function carryOver(next: Engine, old: Engine, oldGlobe: Globe, nextGlobe: Globe): void {
  next.cam = old.cam;
  next.flat = old.flat;
  next.rings = old.rings;
  next.ringRot = old.ringRot;
  next.picture = old.picture;
  next.fitK = old.fitK;
  next.key = old.key;
  nextGlobe.nodes.forEach((node, i) => {
    const was = oldGlobe.byKey.get(node.key);
    if (!was || was.index >= old.n) {
      next.fresh[i] = 1;
      next.A[i] = 0;
      return;
    }
    const j = was.index;
    next.X[i] = old.X[j];
    next.Y[i] = old.Y[j];
    next.R[i] = old.R[j];
    next.Z[i] = old.Z[j];
    next.A[i] = old.A[j];
  });
  beginTransition(next, performance.now(), 420);
}

/** New nodes appear where they belong, not flying in from the origin: called once the targets are known. */
export function placeFresh(e: Engine): void {
  for (let i = 0; i < e.n; i++) {
    if (!e.fresh[i]) continue;
    e.fresh[i] = 0;
    e.X[i] = e.fx[i] = e.TX[i];
    e.Y[i] = e.fy[i] = e.TY[i];
    e.R[i] = e.fr[i] = e.TR[i];
    e.Z[i] = e.TZ[i];
  }
}

/* ── Sizes ───────────────────────────────────────────────────────────── */

/** A node's radius on the globe at its depth, in px: the memory shrinks as it grows so a big graph stays a texture. */
function globeSizes(globe: Globe, rpx: number): Record<GlobeLayer, number> {
  const m = Math.max(1, globe.counts.memory);
  const spacing = Math.sqrt((4 * Math.PI * LAYER_RADIUS.memory * LAYER_RADIUS.memory) / m) * rpx;
  const mem = Math.max(1.15, Math.min(7, spacing * 0.3));
  const outer = Math.max(5, Math.min(15, rpx * 0.022));
  return { memory: mem, skills: Math.max(3, Math.min(11, rpx * 0.014)), apps: Math.max(9, Math.min(24, rpx * 0.034)), routines: Math.max(5, Math.min(13, rpx * 0.019)), roadmap: outer, architecture: outer, plans: outer, decisions: outer, library: outer };
}

/** A bubble's size next to a plain dot (1 for a light node, up to 2.7). */
export const bubbleFactor = (weight: number): number => bubbleRadius(weight) / 2.8;

/** The smallest a layer is drawn on the map, so tools and routines stay readable when the whole map is in view. */
const MAP_MIN: Record<GlobeLayer, number> = { memory: 2.1, skills: 3.4, apps: 11, routines: 6.5, roadmap: 6.5, architecture: 6.5, plans: 6.5, decisions: 6.5, library: 6.5 };

/* ── Targets ─────────────────────────────────────────────────────────── */

export interface TargetInput {
  globe: Globe;
  stage: Stage;
  picture: Picture;
  map: MapLayout | null;
  tl: TimelineLayout | null;
  /** The Timeline's place in the stage: its left edge. */
  tlX: number;
  offLayers: ReadonlySet<GlobeLayer>;
  offDepts: ReadonlySet<number>;
  /** What a closed folder hides (1 for hidden): `foldHidden`. */
  fold?: Uint8Array;
  /** The level that is open (the rim state), when one is. */
  rimFocus?: OpenFocus | null;
  motion: boolean;
}

const hidden = (n: GlobeNode, off: ReadonlySet<GlobeLayer>, offDepts: ReadonlySet<number>, fold?: Uint8Array) => off.has(n.layer) || (n.deptIndex >= 0 && offDepts.has(n.deptIndex)) || !!fold?.[n.index];

/** Where a node is on the globe now, in globe units: on the rim state's ring or compressed shell when a level is open. */
export function nodePosition(e: Pick<Engine, 'rings' | 'rim'>, globe: Globe, n: GlobeNode): { x: number; y: number; z: number } {
  return e.rim?.places.get(n.index) ?? positionOf(n, e.rings, e.rim?.radii ?? globe.radii);
}

/** A hub or a folder on the globe, in px: by what it holds. */
function globeStructSize(n: GlobeNode, rpx: number): number {
  const k = n.struct === 'dir' ? 0.016 : 0.022;
  return Math.max(n.struct === 'dir' ? 6 : 8, Math.min(n.struct === 'dir' ? 15 : 20, rpx * k * (1 + Math.sqrt(n.weight) / 7)));
}

/** The globe's centre and radius for a stage, at zoom 1. */
export function globeFrame(stage: Stage, outer: number): { cx: number; cy: number; r: number } {
  const room = roomOf(stage);
  // The rings are tilted, so the outer ones reach further sideways than up: width fits the whole, height a little over two thirds of it.
  const r = Math.min((room.w / 2) * 0.94 / outer, (room.h / 2) * 0.94 / Math.max(0.78 * outer, 0.9));
  return { cx: room.x + room.w / 2, cy: room.y + room.h / 2, r };
}

/** Project a point of the globe (units) to the screen under a camera: x, y, depth in px, perspective scale. */
export function projectGlobe(p: { x: number; y: number; z: number }, cam: GlobeCamera, frame: { cx: number; cy: number; r: number }): { x: number; y: number; z: number; s: number } {
  const R = frame.r * cam.zoom;
  const v = rotatePoint(p, cam.yaw, cam.pitch);
  const zx = v.z * R;
  const s = 1 / (1 - zx / Math.max(900, R * 4.2));
  return { x: frame.cx + cam.panX + v.x * R * s, y: frame.cy + cam.panY - v.y * R * s, z: zx, s };
}

/** Fill every node's target place, size, depth and visibility for the picture. */
export function computeTargets(e: Engine, t: TargetInput): void {
  const { globe, stage } = t;
  const room = roomOf(stage);
  e.picture = t.picture;
  if (t.picture === 'globe') {
    const frame = globeFrame(stage, globe.outer);
    e.gR = frame.r;
    e.rim = t.rimFocus ? globeRim(globe, t.rimFocus) : null;
    const sizes = globeSizes(globe, frame.r * e.cam.zoom);
    for (const n of globe.nodes) {
      const i = n.index;
      const p = projectGlobe(nodePosition(e, globe, n), e.cam, frame);
      e.TX[i] = p.x;
      e.TY[i] = p.y;
      e.TZ[i] = p.z;
      const base = n.struct ? globeStructSize(n, frame.r * e.cam.zoom) : sizes[n.layer] * (n.layer === 'memory' ? bubbleFactor(n.weight) : 1);
      e.TR[i] = base * Math.min(1.6, Math.max(0.7, p.s));
      // A layer's hub has no place on the globe: the layer's name and its shell say it.
      e.TA[i] = n.struct === 'layer' || n.struct === 'theme' || hidden(n, t.offLayers, t.offDepts, t.fold) ? 0 : 1;
    }
    const c = projectGlobe({ x: 0, y: 0, z: 0 }, e.cam, frame);
    e.cx = c.x;
    e.cy = c.y;
    return;
  }
  if (t.picture === 'map' && t.map) {
    const m = t.map;
    const wob = t.motion ? 1.4 : 0;
    for (const n of globe.nodes) {
      const i = n.index;
      const place = m.kind === 'rings' ? ringsPlace(m, i, e.ringRot) : { x: m.xs[i], y: m.ys[i] };
      const w = wob ? Math.sin(e.clock * 0.9 + e.seed[i] * 40) * wob : 0;
      const w2 = wob ? Math.cos(e.clock * 0.7 + e.seed[i] * 31) * wob : 0;
      e.TX[i] = (place.x + w) * e.flat.k + e.flat.x;
      e.TY[i] = (place.y + w2) * e.flat.k + e.flat.y;
      e.TZ[i] = 0;
      const floor = n.struct === 'layer' || n.struct === 'theme' ? 10 : n.struct === 'group' ? 8 : n.struct === 'dir' ? 7 : MAP_MIN[n.layer];
      e.TR[i] = Math.max(m.scale ? floor * m.scale[i] : floor, mapRadiusOf(n) * (m.scale?.[i] ?? 1) * m.unit * e.flat.k * (n.layer === 'memory' ? 1.3 : 1.1));
      e.TA[i] = hidden(n, t.offLayers, t.offDepts, t.fold) ? 0 : 1;
    }
    e.cx = e.flat.x;
    e.cy = e.flat.y;
    return;
  }
  if (t.picture === 'timeline' && t.tl) {
    const tl = t.tl;
    for (const n of globe.nodes) {
      const i = n.index;
      const pos = tl.positions.get(i);
      if (!pos) {
        e.TX[i] = e.X[i];
        e.TY[i] = e.Y[i];
        e.TR[i] = e.R[i];
        e.TZ[i] = 0;
        e.TA[i] = 0;
        continue;
      }
      e.TX[i] = (pos.x + t.tlX) * e.flat.k + e.flat.x;
      e.TY[i] = pos.y * e.flat.k + e.flat.y;
      e.TZ[i] = 0;
      e.TR[i] = Math.max(pos.icon ? 6 : 1.4, pos.r * e.flat.k * (n.layer === 'apps' || n.layer === 'routines' ? 0.95 : 1.05));
      e.TA[i] = 1;
    }
    const lane = tl.lanes.find((l) => l.key === 'instructions');
    e.cx = ((tl.today?.x ?? tl.bounds.right) + t.tlX) * e.flat.k + e.flat.x;
    e.cy = lane ? ((lane.top + lane.bottom) / 2) * e.flat.k + e.flat.y : e.flat.y;
    return;
  }
  void room;
}

/** The camera that fits the picture in the room between the panels. */
export function fitFlat(e: Engine, t: Pick<TargetInput, 'stage' | 'map' | 'tl' | 'picture' | 'tlX'>): FlatCamera {
  const room = roomOf(t.stage);
  if (t.picture === 'map' && t.map) {
    const f = fitMap(t.map.extent, room.w, room.h, 70);
    return { k: f.k, x: room.x + room.w / 2, y: room.y + room.h / 2 };
  }
  void e;
  return { k: 1, x: 0, y: 0 };
}

/** Start a picture change: keep where everything is and blend to the new places. */
export function beginTransition(e: Engine, now: number, dur: number): void {
  e.fx.set(e.X);
  e.fy.set(e.Y);
  e.fr.set(e.R);
  e.trans = dur > 0 ? { t0: now, dur } : null;
}

/** Move the drawn places towards the targets: a blend when a transition runs, else straight there. Returns true while anything is still moving. */
export function settle(e: Engine, now: number, dt: number): boolean {
  let moving = false;
  const tr = e.trans;
  if (tr) {
    const p = tweenProgress(tr.t0, now, tr.dur);
    const ease = easeInOutCubic(p);
    blendPlaces(e.fx, e.fy, e.TX, e.TY, ease, e.X, e.Y, e.delay);
    for (let i = 0; i < e.n; i++) {
      const staggered = Math.max(0, Math.min(1, (ease - e.delay[i] * 0.35) / 0.65));
      e.R[i] = e.fr[i] + (e.TR[i] - e.fr[i]) * staggered;
      e.Z[i] = e.TZ[i];
    }
    if (p >= 1) e.trans = null;
    moving = true;
  } else {
    e.X.set(e.TX);
    e.Y.set(e.TY);
    e.R.set(e.TR);
    e.Z.set(e.TZ);
  }
  // Visibility fades rather than cuts.
  const k = Math.min(1, dt * 9);
  for (let i = 0; i < e.n; i++) {
    const d = e.TA[i] - e.A[i];
    if (Math.abs(d) > 0.004) {
      e.A[i] += d * k;
      moving = true;
    } else e.A[i] = e.TA[i];
  }
  return moving;
}

/* ── Camera tweens ───────────────────────────────────────────────────── */

export function flyGlobeCamera(e: Engine, to: GlobeCamera, now: number, dur: number, follow?: number): void {
  const t = clampCamera(to);
  if (dur <= 0) {
    e.cam = t;
    e.camTween = null;
    return;
  }
  e.camTween = { t0: now, dur, from: { ...e.cam }, to: t, ...(follow !== undefined ? { follow } : {}) };
}

export function flyFlatCamera(e: Engine, to: FlatCamera, now: number, dur: number): void {
  if (dur <= 0) {
    e.flat = to;
    e.flatTween = null;
    return;
  }
  e.flatTween = { t0: now, dur, from: { ...e.flat }, to };
}

export const flatEase = easeOutCubic;

/** The flat camera that puts the screen point (px, py) of the *world* place (wx, wy) at the middle of the room, at zoom `k`. */
export function centreOn(wx: number, wy: number, k: number, stage: Stage): FlatCamera {
  const room = roomOf(stage);
  return { k, x: room.x + room.w / 2 - wx * k, y: room.y + room.h / 2 - wy * k };
}

/** The nearest visible node to a pointer, front-most on a tie (the globe's back side is not pickable). */
export function pickNode(e: Engine, globe: Globe, px: number, py: number, picture: Picture): number {
  let best = -1;
  let bestScore = Infinity;
  for (let i = 0; i < e.n; i++) {
    if (e.A[i] < 0.3) continue;
    const dx = e.X[i] - px;
    const dy = e.Y[i] - py;
    const reach = Math.max(6, e.R[i] * (globe.nodes[i].layer === 'routines' ? 1.5 : 1.25)) + 2;
    const d2 = dx * dx + dy * dy;
    if (d2 > reach * reach) continue;
    // The far side of the globe is not pickable: only what faces the viewer.
    if (picture === 'globe' && e.Z[i] < -e.gR * 0.05) continue;
    // The nearer in depth wins when two overlap on screen.
    const score = Math.sqrt(d2) - (picture === 'globe' ? e.Z[i] * 0.02 : 0) - e.R[i] * 0.3;
    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  }
  return best;
}
